import {
  resolveCartInventoryStatusLabel,
  resolveInventoryAvailability,
} from '~/utils/store/inventoryAvailability'
import { useUserStore } from '~/store/common/user'
import { defineStore } from 'pinia'
import { cartApi } from '~/api/store/cart'
import { BaseMessage } from '~/utils/baseMessage'
import { trackAddToCartSuccess } from '~/utils/tracker'
import { isDeepEqual, debounce } from '~/utils'
import CommonModal from '~/utils/commonModal'
import {
  fetchProductDetailForCartPromotion,
  hasExplicitProductStockForCart,
  normalizeAddToCartPromotionProduct,
  resolveListAddToCartSku,
  resolveProductSkuId,
  resolveSkuSnapshotFromProduct,
  stripActivityDimensionsForCartAdd,
  validateAddToCartStock,
} from '~/utils/product'
import { normalizeShopCartInfoV3, isCartHasExpiredPromotion } from '~/utils/shopCartInfoAdapter'
import { isCartComboPackageActivityInvalid } from '~/utils/store/promotionActivityInProgress'
import {
  getHeaderCartDisplayItems,
  resolveHeaderCartFinalMoney,
  sumHeaderCartQuantity,
} from '~/utils/store/headerCartView'
import { buildCartSpecChangeMutation } from '~/utils/store/cartSpecChangePayload'
import { buildCartComboGroupMutation } from '~/utils/store/cartComboGroupMutation'
import { type3PackageGroupKeyFromRow } from '~/utils/store/type3PackageGroupKey'
import { isComboPackage } from '~/utils/store/promotionTypes'
import { PRODUCT_DELISTED_FLAG, SYNC_CHANNELS, SYNC_MESSAGES, TAB_ID } from '~/utils/constant'

/**
 * @description 当前是否在商城购物车页（仅该页展示活动过期刷新提示）
 * @returns {boolean}
 */
const isStoreCartRouteActive = () => {
  if (!import.meta.client) return false
  try {
    const route = useRoute()
    const path = String(route?.path || '').replace(/\/$/, '')
    return path === '/store/cart'
  } catch {
    return false
  }
}

/**
 * @description 构造 V3 `changeItem/v3` 请求体：仅更新勾选时与改数量、改规格入参字段对齐
 * @param {Object} item - 扁平行购物车项
 * @param {boolean} nextChecked - 目标是否勾选
 * @returns {Object}
 */
const buildChangeItemPayloadForChecked = (item, nextChecked) => ({
  basketId: item.basketId,
  prodId: item.prodId,
  skuId: item.skuId,
  basketCount: Math.max(1, Math.trunc(Number(item.quantity) || 1)),
  isChecked: nextChecked ? 1 : 0,
  promotionType: Number(item.promotionType) || 0,
  ...((item.promotionCode ?? item.promotionId) != null
    ? { promotionCode: item.promotionCode ?? item.promotionId }
    : {}),
  ...(item.comboKey != null && item.comboKey !== '' ? { comboKey: item.comboKey } : {}),
})

const cartItemPackageGroupKey = (item) =>
  isComboPackage(item?.promotionType)
    ? item?.comboGroupKey || type3PackageGroupKeyFromRow(item) || null
    : null

const isSameCartSkuId = (left, right) => {
  if (left == null || right == null) return false
  return String(left) === String(right)
}

/**
 * @description 解析购物车行库存（扁平行 `stocks` / `stock`，来源为接口 `skuVO.stocks`）；无法解析时返回 null
 * @param {Object|null|undefined} item
 * @returns {number|null}
 */
export const resolveCartLineStock = (item) => {
  if (!item || typeof item !== 'object') return null
  const raw = item.stocks ?? item.stock
  if (raw == null || raw === '') return null
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) && n >= 0 ? n : null
}

/**
 * @description SPU/SKU 状态异常或活动过期（与库存无关）
 * @param {Object|null|undefined} item
 * @returns {boolean}
 */
export const isCartLineStatusUnavailable = (item) =>
  !!item?.isExpired || !!item?.cartLineStatusInvalid

/**
 * @description 商品有效但库存为 0（售罄），按快照身份豁免代理商
 * @param {Object|null|undefined} item
 * @returns {boolean}
 */
export const isCartLineSoldOut = (item) =>
  resolveInventoryAvailability(item, { legacyQuantity: resolveCartLineStock(item) }).soldOut

export const isCartLineInventoryUpdating = (item) => resolveInventoryAvailability(item).updating

/**
 * @description 判断商品是否不可用：下架/失效/过期，或库存售罄（展示与交互同失效行）
 * @param {Object} item - 商品对象
 * @returns {boolean}
 */
export const isOffShelf = (item) => isCartLineStatusUnavailable(item) || isCartLineSoldOut(item)

/**
 * @description 购物车行是否禁止改数量等操作（下架/售罄或套餐活动已失效）；减号仅引导删除，不改数量
 * @param {Object|null|undefined} item
 * @returns {boolean}
 */
export const isCartLineInteractionBlocked = (item) =>
  isOffShelf(item) || isCartLineInventoryUpdating(item) || isCartComboPackageActivityInvalid(item)

/**
 * @description 购物车行复选框是否禁用（下架/活动失效不可勾选；售罄仍允许取消勾选）
 * @param {Object|null|undefined} item
 * @returns {boolean}
 */
export const isCartLineCheckboxDisabled = (item) => isCartLineInteractionBlocked(item)

/**
 * @description 判断购物车商品是否为已下架且已退市
 * @param {Object} item - 商品对象
 * @returns {boolean}
 */
export const isDelisted = (item) => {
  return (
    isCartLineStatusUnavailable(item) && Number(item?.isDelisted) === PRODUCT_DELISTED_FLAG.DELISTED
  )
}

/**
 * @description 购物车状态标签：失效优先，其次库存更新、售罄或正库存不足数量
 * @param {Object} item - 商品对象
 * @returns {string}
 */
export const getCartItemStatusLabel = (item) => {
  if (isCartLineStatusUnavailable(item)) {
    return isDelisted(item) ? '已退市' : '已下架'
  }
  return resolveCartInventoryStatusLabel(item)
}

/**
 * @description 判断购物车商品是否允许跳转商详
 * @param {Object} item - 商品对象
 * @returns {boolean}
 */
export const canViewCartItemDetail = (item) => {
  const prodId = item?.prodId ?? item?.id
  return (
    !!prodId && (!isCartLineStatusUnavailable(item) || isDelisted(item) || isCartLineSoldOut(item))
  )
}

/**
 * @description 商城购物车存储
 */
export const useStoreCartStore = defineStore('storeCart', {
  state: () => ({
    // 原始购物车响应数据
    cartData: null,
    // 购物车项列表（扁平化后用于表格显示）
    items: [],
    // 选中商品 ID (basketId)
    selectedIds: [],
    // 是否加载中
    loading: false,
    // 是否正在请求购物车数据
    fetching: false,
    // 是否正在执行购物车变更操作
    actionLoading: false,
    // 购物车请求 Promise，防止重复请求
    cartPromise: null,
    headerCartData: null,
    headerItems: [],
    headerFetching: false,
    headerCartPromise: null,
  }),
  getters: {
    /**
     * @description 购物车商品总数（基于 items 遍历累加 quantity，排除失效商品）
     */
    totalCount: (state) =>
      state.items
        .filter((item) => !isCartLineInteractionBlocked(item))
        .reduce((sum, item) => sum + (item.quantity || 0), 0),
    /**
     * @description 商品总额（购物车接口根字段 data.totalMoney）
     */
    totalMoney: (state) => state.cartData?.totalMoney || 0,
    /**
     * @description 应付总价（购物车接口根字段 data.totalFinalAmount）
     */
    finalMoney: (state) => {
      const n = Number(state.cartData?.totalFinalAmount)
      return Number.isFinite(n) ? n : 0
    },
    selectedItems: (state) =>
      state.items.filter(
        (item) => state.selectedIds.includes(item.id) && !isCartLineInteractionBlocked(item)
      ),
    /**
     * @description 选中商品总数 (总数量)
     */
    selectedCount: (state) =>
      state.items.reduce((sum, item) => {
        if (state.selectedIds.includes(item.id) && !isCartLineInteractionBlocked(item)) {
          return sum + (item.quantity || 0)
        }
        return sum
      }, 0),
    /**
     * @description 选中商品应付总价（与接口 data.totalFinalAmount 同源，兼容旧命名）
     */
    selectedTotalPrice: (state) => {
      const n = Number(state.cartData?.totalFinalAmount)
      return Number.isFinite(n) ? n : 0
    },
    headerDisplayItems: (state) => getHeaderCartDisplayItems(state.headerItems),
    headerTotalCount: (state) => sumHeaderCartQuantity(state.headerItems),
    headerFinalMoney: (state) => resolveHeaderCartFinalMoney(state.headerCartData),
    /**
     * @description 优惠总额
     */
    totalReduceMoney: (state) => state.cartData?.totalReduceMoney || 0,
    /**
     * @description 运费
     */
    freightAmount: (state) => state.cartData?.freightAmount || 0,
    /**
     * @description 包邮最低额度（元）
     */
    freeShippingThreshold: (state) => state.cartData?.freeShippingThreshold,
    /** @description 后端运费试算是否命中模板 */
    freightMatched: (state) => state.cartData?.freightMatched === true,
    /** @description 后端权威包邮结果，禁止前端按当前门槛反推 */
    isFreeShipping: (state) => state.cartData?.isFreeShipping === true,
    /** @description 运费说明（无地址提示或未包邮差额） */
    freightTip: (state) => state.cartData?.freightTip || null,
    /**
     * @description 优惠券优惠总额（V3 各行 `couponReduceAmount` 之和）
     */
    couponReduceAmount: (state) => state.cartData?.couponReduceAmount || 0,
    /**
     * @description 可用优惠券列表（V3 根字段 `coupons`）
     * @returns {Array<Object>}
     */
    availableCoupons: (state) =>
      Array.isArray(state.cartData?.coupons) ? state.cartData.coupons : [],
    /**
     * @description 是否部分选中 (排除已下架商品)
     */
    isPartiallySelected: (state) => {
      const availableItems = state.items.filter((item) => !isCartLineInteractionBlocked(item))
      if (availableItems.length === 0) return false
      const selectedAvailableCount = availableItems.filter((item) =>
        state.selectedIds.includes(item.id)
      ).length
      return selectedAvailableCount > 0 && selectedAvailableCount < availableItems.length
    },
    /**
     * @description 是否全选 (排除已下架商品)
     */
    isAllSelected: (state) => {
      const availableItems = state.items.filter((item) => !isCartLineInteractionBlocked(item))
      return (
        availableItems.length > 0 &&
        availableItems.every((item) => state.selectedIds.includes(item.id))
      )
    },
    /**
     * @description 购物车是否处于交互锁定中
     * @returns {boolean}
     */
    isInteractionLocked: (state) => state.loading || state.fetching || state.actionLoading,
  },
  actions: {
    /**
     * @description 加载购物车数据
     * @param {number} addrId - 地址 ID
     * @param {boolean} silent - 是否静默刷新（不触发 loading 状态）
     * @param {boolean} isFromSync - 是否来自多页签同步（防止循环同步）
     */
    async fetchCart(addrId = 0, silent = false, isFromSync = false) {
      // 如果正在请求中且 addrId 相同，返回现有的 Promise
      if (this.cartPromise) {
        return this.cartPromise
      }

      this.cartPromise = (async () => {
        this.fetching = true
        if (!silent) this.loading = true
        try {
          await useUserStore().ensureStoreAgencyIdentity()
          const res = await cartApi.getCartInfo(addrId)
          const cartPayload = res ?? {}

          const normalized = normalizeShopCartInfoV3(cartPayload, {
            isAgencyUser: useUserStore().isAgencyUser,
          })
          const nextCartData = normalized.cartData
          const flatItems = normalized.flatItems
          const selectedIds = normalized.selectedIds

          if (!isDeepEqual(nextCartData, this.cartData)) {
            this.cartData = nextCartData
          }
          if (!isDeepEqual(flatItems, this.items)) {
            this.items = flatItems
          }
          if (!isDeepEqual(selectedIds, this.selectedIds)) {
            this.selectedIds = selectedIds
          }

          if (
            import.meta.client &&
            isStoreCartRouteActive() &&
            isCartHasExpiredPromotion(cartPayload)
          ) {
            BaseMessage.info('活动已过期，订单已刷新')
          }

          return nextCartData
        } catch (error) {
          console.error('[CartStore] Failed to fetch cart:', error)
          throw error
        } finally {
          this.fetching = false
          if (!silent) this.loading = false
          this.cartPromise = null

          // 多页签同步：通知其他页面刷新购物车数据
          if (import.meta.client && !isFromSync) {
            const channel = new BroadcastChannel(SYNC_CHANNELS.CART)
            channel.postMessage({
              type: SYNC_MESSAGES.REFRESH_CART,
              from: TAB_ID,
            })
            channel.close()
          }
        }
      })()

      return this.cartPromise
    },

    async fetchHeaderCart(addrId = 0) {
      if (this.headerCartPromise) {
        return this.headerCartPromise
      }

      this.headerCartPromise = (async () => {
        this.headerFetching = true
        try {
          await useUserStore().ensureStoreAgencyIdentity()
          const res = await cartApi.getHeaderCartInfo(addrId)
          const normalized = normalizeShopCartInfoV3(res ?? {}, {
            isAgencyUser: useUserStore().isAgencyUser,
          })
          const nextHeaderCartData = normalized.cartData
          const nextHeaderItems = normalized.flatItems

          if (!isDeepEqual(nextHeaderCartData, this.headerCartData)) {
            this.headerCartData = nextHeaderCartData
          }
          if (!isDeepEqual(nextHeaderItems, this.headerItems)) {
            this.headerItems = nextHeaderItems
          }

          return nextHeaderCartData
        } catch (error) {
          console.error('[CartStore] Failed to fetch header cart:', error)
          throw error
        } finally {
          this.headerFetching = false
          this.headerCartPromise = null
        }
      })()

      return this.headerCartPromise
    },

    /**
     * @description 以互斥方式执行购物车变更操作，并在成功后刷新服务端购物车数据
     * @param {Function} task - 实际变更请求
     * @param {string} errorMessage - 失败提示文案
     * @returns {Promise<boolean>}
     */
    async runCartMutation(task, errorMessage = '购物车操作失败') {
      if (this.isInteractionLocked) {
        return false
      }

      this.actionLoading = true
      try {
        await task()
        await this.fetchCart(0, true)
        await this.fetchHeaderCart(0).catch(() => {})
        return true
      } catch (error) {
        console.error('[CartStore] Cart mutation failed:', errorMessage, error)
        await this.fetchCart(0, true).catch(() => {})
        // 业务错误由 api/axios 响应拦截器统一反馈，此处不再重复 BaseMessage.error
        return false
      } finally {
        this.actionLoading = false
      }
    },

    /**
     * @description 更新商品数量（V3：`changeItem/v3` 改为绝对值 `basketCount`），并在请求完成后以服务端结果回填购物车
     * @param {number|string} basketId - 购物车项 ID
     * @param {number} quantity - 新数量（≤0 时自动转为删除操作）
     * @returns {Promise<boolean>}
     */
    async updateQuantity(basketId, quantity) {
      const item = this.items.find((i) => i.id === basketId)
      if (!item || isCartLineInteractionBlocked(item)) return false

      const raw = Math.trunc(Number(quantity))
      const nextCount = Number.isFinite(raw) ? Math.max(0, raw) : 0

      if (nextCount === 0) {
        return this.removeFromCart(item.basketId)
      }

      const inventory = resolveInventoryAvailability(item, {
        isAgencyUser: useUserStore().isAgencyUser,
        legacyQuantity: item.stock,
      })
      if (nextCount > inventory.maxQuantity) {
        BaseMessage.warning(`该商品最多购买${inventory.maxQuantity}台`)
        return false
      }
      if (nextCount === Number(item.quantity)) return true

      return this.runCartMutation(async () => {
        await cartApi.changeItems({
          basketId: item.basketId,
          prodId: item.prodId,
          skuId: item.skuId,
          basketCount: nextCount,
          isChecked: item.checked ? 1 : 0,
          promotionType: Number(item.promotionType) || 0,
          ...((item.promotionCode ?? item.promotionId) != null
            ? { promotionCode: item.promotionCode ?? item.promotionId }
            : {}),
          ...(item.comboKey != null && item.comboKey !== '' ? { comboKey: item.comboKey } : {}),
        })
      }, '更新数量失败')
    },

    /**
     * @description 保存套餐/组合抽屉的整组选择与数量：以 leader basketId + 完整 comboItems 单请求原子替换。
     * @param {Object} group - 购物车套餐/组合分组
     * @param {Array<Object>} selectedRows - 抽屉内已选商品
     * @param {(row: Object) => number} getQuantity - 读取抽屉商品数量
     * @returns {Promise<boolean>}
     */
    async saveComboGroup(group, selectedRows, getQuantity) {
      let mutation
      try {
        mutation = buildCartComboGroupMutation({ group, selectedRows, getQuantity })
      } catch (error) {
        console.error('[CartStore] Invalid combo group mutation:', error)
        return false
      }

      return this.runCartMutation(async () => {
        await cartApi.changeItems(mutation.changeItems)
      }, '保存组合促销商品失败')
    },

    /**
     * @description 修改商品规格：普通行先加新 SKU 再删旧行；套餐/组合行通过 leader basketId + 完整 comboItems 原子替换整组。
     * @param {number|string} basketId - 购物车项 ID
     * @param {number|string} newSkuId - 新的 SKU ID
     * @returns {Promise<boolean>}
     */
    async updateItemSpec(basketId, newSkuId, options = {}) {
      const item = this.items.find((i) => i.id === basketId)
      if (!item || isCartLineInteractionBlocked(item) || isSameCartSkuId(item.skuId, newSkuId)) {
        return false
      }

      return this.runCartMutation(async () => {
        const comboGroupKey = cartItemPackageGroupKey(item)
        const groupRows = comboGroupKey
          ? this.items.filter((row) => cartItemPackageGroupKey(row) === comboGroupKey)
          : []
        const mutation = buildCartSpecChangeMutation(item, newSkuId, {
          ...options,
          groupRows,
          leaderBasketId: item.comboGroupLeaderBasketId,
        })
        if (mutation.mode === 'replace') {
          await cartApi.changeItems(mutation.changeItems)
          return
        }

        await cartApi.changeItems(mutation.addPayload)
        await cartApi.deleteItems(mutation.deleteBasketIds)
      }, '规格修改失败')
    },

    /**
     * @description 切换选中状态
     * @param {number|string} basketId - 购物车项 ID
     * @returns {Promise<boolean>}
     */
    async toggleSelect(basketId) {
      const item = this.items.find((i) => i.id === basketId)
      if (!item || isCartLineCheckboxDisabled(item)) return false

      const isChecked = !this.selectedIds.includes(basketId)
      return this.runCartMutation(async () => {
        await cartApi.changeItems(buildChangeItemPayloadForChecked(item, isChecked))
      }, '更新勾选状态失败')
    },

    /**
     * @description 套餐组勾选：同一「promotionType + promotionCode + comboKey」多行共用一组状态，与首行勾选一致，一次 `changeItem/v3` 批量同步后端
     * @param {number|string} basketId - 任一组内 basketId
     * @returns {Promise<boolean>}
     */
    async toggleComboGroupSelection(basketId) {
      const item = this.items.find((i) => i.id === basketId)
      if (!item) return false
      const key = cartItemPackageGroupKey(item)
      if (!key) return this.toggleSelect(basketId)

      const leader = this.items.find(
        (i) =>
          cartItemPackageGroupKey(i) === key &&
          !isCartLineCheckboxDisabled(i) &&
          (i.comboGroupLeaderBasketId == null ||
            Number(i.comboGroupLeaderBasketId) === Number(i.basketId ?? i.id))
      )
      if (!leader) return this.toggleSelect(basketId)

      const group = this.items.filter(
        (i) => cartItemPackageGroupKey(i) === key && !isCartLineCheckboxDisabled(i)
      )
      if (group.length === 0) return false

      const isChecked = !this.selectedIds.includes(leader.id)
      const payloads = group.map((i) => buildChangeItemPayloadForChecked(i, isChecked))

      return this.runCartMutation(async () => {
        await cartApi.changeItems(payloads)
      }, '更新勾选状态失败')
    },

    /**
     * @description 全选/取消全选 (排除已下架商品)
     * @param {boolean} selected - 是否全选
     * @returns {Promise<boolean>}
     */
    async toggleSelectAll(selected) {
      let targetItems = []

      if (selected) {
        // 全选：仅作用于上架商品
        targetItems = this.items.filter((item) => !isCartLineInteractionBlocked(item))
        if (targetItems.length === 0) return false
      } else {
        // 取消全选：作用于所有当前已选中的商品（包含可能已下架但仍处于选中状态的商品）
        targetItems = this.items.filter((item) => this.selectedIds.includes(item.id))
      }

      const payloads = targetItems.map((item) => buildChangeItemPayloadForChecked(item, selected))

      if (payloads.length === 0) return false

      return this.runCartMutation(async () => {
        await cartApi.changeItems(payloads)
      }, '更新全选状态失败')
    },

    /**
     * @description 从购物车移除商品（支持单条或批量 basketId）
     * @param {number|string|Array<number|string>} basketId - 购物车项 ID 或 ID 列表
     * @returns {Promise<boolean>}
     */
    async removeFromCart(basketId) {
      const ids = (Array.isArray(basketId) ? basketId : [basketId]).filter(
        (id) => id != null && id !== ''
      )
      if (!ids.length) return false

      return this.runCartMutation(async () => {
        await cartApi.deleteItems(ids)
      }, '移除商品失败')
    },

    /**
     * @description 清空购物车（移除选中的商品）
     * @returns {Promise<boolean>}
     */
    async clearCart() {
      if (this.selectedIds.length === 0) return false

      return this.runCartMutation(async () => {
        await cartApi.deleteItems(this.selectedIds)
      }, '清空购物车失败')
    },

    /**
     * @description 清除购物车本地状态（仅用于退出登录等场景）
     */
    clearCartState() {
      this.cartData = null
      this.items = []
      this.selectedIds = []
      this.loading = false
      this.fetching = false
      this.actionLoading = false
      this.cartPromise = null
      this.headerCartData = null
      this.headerItems = []
      this.headerFetching = false
      this.headerCartPromise = null
    },

    /**
     * @description 加入购物车（V3：单条 `changeItem/v3`）
     * @param {Object} product - 商品信息
     * @param {number} [product.id] - 商品ID
     * @param {number} [product.prodId] - 商品ID (兼容性)
     * @param {number} [product.skuId] - SKU ID（列表优先价格默认规格，售罄时改选有货规格）
     * @param {boolean} [product.selectAvailableSku] - 仅列表快捷加购自动选择有货规格
     * @param {number} [product.quantity] - 数量
     * @param {number} [product.stocks] - 库存
     * @param {number} [product.totalStocks] - 总库存
     * @param {0|1|2|3|4|5} [product.promotionType] - 活动类型（TDC）：0 无，1 满量特价，2 满量折扣，3 买赠，4 搭配套餐，5 组合促销（默认 0）
     * @param {number|string} [product.promotionCode] - 活动编码（套餐/组合时必填）
     * @param {number|string} [product.promotionId] - 活动编码（兼容旧版，套餐/组合时必填）
     */
    async addToCart(product) {
      const userStore = useUserStore()
      if (!userStore.isLoggedIn) {
        if (import.meta.client) {
          const { navigateToLogin } = useAuthLinks()
          navigateToLogin()
        }
        return
      }
      await userStore.ensureStoreAgencyIdentity()

      const extraSources = Array.isArray(product.extraSources) ? product.extraSources : []
      const skipStockConfirm = product.skipStockConfirm === true
      const trackConfig = product.track || null
      const selectAvailableSku = product.selectAvailableSku === true && !userStore.isAgencyUser
      const {
        extraSources: _extraSources,
        skipStockConfirm: _skip,
        track: _track,
        selectAvailableSku: _selectAvailableSku,
        ...productInput
      } = product

      const prodId = productInput.prodId || productInput.id
      let validationExtra = extraSources
      let validationPayload = productInput

      if (prodId) {
        const preliminarySku = resolveSkuSnapshotFromProduct(validationPayload)
        const needsDetail =
          selectAvailableSku ||
          resolveProductSkuId(validationPayload) == null ||
          !hasExplicitProductStockForCart(validationPayload, preliminarySku)
        if (needsDetail) {
          try {
            const { detail, extraSources: fetched } =
              await fetchProductDetailForCartPromotion(prodId)
            if (detail && typeof detail === 'object') {
              validationPayload = selectAvailableSku
                ? {
                    ...productInput,
                    ...detail,
                    priceSkuId: productInput.priceSkuId,
                    skuId: productInput.skuId,
                    quantity: productInput.quantity,
                  }
                : { ...detail, ...productInput }
              validationExtra = Array.isArray(fetched) ? fetched : []
            } else if (selectAvailableSku) {
              BaseMessage.error('获取商品规格库存失败，请稍后重试')
              return
            }
          } catch {
            if (selectAvailableSku) {
              BaseMessage.error('获取商品规格库存失败，请稍后重试')
              return
            }
            // 列表快照缺字段时尽力补全；失败则继续用原快照
          }
        }
      }

      if (selectAvailableSku) {
        const sku = resolveListAddToCartSku(validationPayload)
        if (!sku) {
          BaseMessage.error('无法识别商品规格，请从商品详情页选择规格后加入购物车')
          return
        }
        validationPayload = { ...validationPayload, skuId: resolveProductSkuId({ sku }), sku }
      }

      const skuId = resolveProductSkuId(validationPayload)
      if (skuId == null) {
        BaseMessage.error('无法识别商品规格，请从商品详情页选择规格后加入购物车')
        return
      }

      const currentSku = resolveSkuSnapshotFromProduct({
        ...validationPayload,
        skuId,
      })
      const basketQty = Math.max(1, Math.trunc(Number(validationPayload.quantity) || 1))
      const stockValidationPayload = stripActivityDimensionsForCartAdd(validationPayload)

      const stockCheck = validateAddToCartStock(stockValidationPayload, {
        extraSources: validationExtra,
        currentSku,
        quantity: basketQty,
        promoCartQty: 0,
        isAgencyUser: userStore.isAgencyUser || productInput.isAgencyUser === true,
      })

      /**
       * @description 提交加购请求（归一化活动维度后调用 changeItem/v3）
       * @param {Object} payload
       */
      const submitPayload = async (payload) => {
        const normalized = normalizeAddToCartPromotionProduct(payload, {
          extraSources: validationExtra,
          currentSku,
          quantity: basketQty,
          silentActivityStockFallback: false,
        })

        const resolvedSkuId = resolveProductSkuId(normalized)
        if (resolvedSkuId == null) {
          BaseMessage.error('无法识别商品规格，请从商品详情页选择规格后加入购物车')
          return
        }

        const item = {
          prodId: normalized.prodId || normalized.id,
          skuId: resolvedSkuId,
          basketCount: Math.max(1, Math.trunc(Number(normalized.quantity) || basketQty)),
          isChecked: 1,
          promotionType: 0,
        }

        this.loading = true
        try {
          const res = await cartApi.changeItems(item)
          if (res !== false) {
            BaseMessage.success('加入购物车成功')
            if (trackConfig) {
              trackAddToCartSuccess({
                ...trackConfig,
                targetId: String(prodId || trackConfig.targetId || ''),
                item: validationPayload,
                product: validationPayload,
                quantity: item.basketCount,
                skuId: resolvedSkuId,
              })
            }
            await this.fetchCart(0, true)
            await this.fetchHeaderCart(0).catch(() => {})
          }
        } catch (error) {
          console.error('[CartStore] addToCart failed:', error)
        } finally {
          this.loading = false
        }
      }

      if (
        !skipStockConfirm &&
        stockCheck.needConfirm &&
        stockCheck.confirmMessage &&
        import.meta.client
      ) {
        CommonModal.confirm({
          title: '提示',
          content: stockCheck.confirmMessage,
          type: 'warning',
          okText: '继续购买',
          cancelText: '取消',
          onOk: () =>
            submitPayload({
              ...productInput,
              ...stockCheck.fallbackPayload,
              promotionType: 0,
              quantity: basketQty,
              skuId,
            }),
        })
        return
      }

      if (!stockCheck.ok) {
        BaseMessage.error(stockCheck.message || '该商品库存不足')
        return
      }

      await submitPayload({ ...productInput, quantity: basketQty, skuId })
    },
  },
})

let timer = null
/**
 * @description 防抖后的购物车刷新函数，用于多页签同步
 * @reason 当一个页签更新了购物车数据，通知其他所有页签静默刷新。
 * 引入 2-5 秒的随机浮动延迟（staggering），有效错开多页签同时发起的 API 请求，保护服务器免受瞬间并发冲击。
 */
const debouncedFetchCart = () => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  // 引入 2-5 秒的随机浮动时间，错开多页签并发请求
  const staggerDelay = 2000 + Math.floor(Math.random() * 3000)
  timer = setTimeout(async () => {
    try {
      const store = useStoreCartStore()
      await store.fetchCart(0, true, true)
      await store.fetchHeaderCart(0).catch(() => {})
    } finally {
      timer = null
    }
  }, staggerDelay)
}

if (import.meta.client && typeof BroadcastChannel !== 'undefined') {
  const channel = new BroadcastChannel(SYNC_CHANNELS.CART)

  channel.onmessage = (event) => {
    const { type, from } =
      typeof event.data === 'string' ? { type: event.data, from: null } : event.data

    if (type === SYNC_MESSAGES.REFRESH_CART && from !== TAB_ID) {
      debouncedFetchCart()
    }
  }
}
